import { Eye, History, Search, SlidersHorizontal } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';
import { PageBody, PageHeader } from '@/components/layout/page-header';
import { TelaProtegidaCarregando } from '@/components/layout/tela-carregando';
import { Segmentado } from '@/components/patterns/segmented';
import { EstadoErro, EstadoVazio } from '@/components/patterns/states';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, Input } from '@/components/ui/field';
import { Skeleton } from '@/components/ui/skeleton';
import { dataSource, PRODUTOS } from '@/data';
import { mensagemDoErro } from '@/data/http';
import type { Audiencia, EventoVisibilidade, Funcionalidade, Visibilidade } from '@/data/types';
import { AUDIENCIAS } from '@/data/types';
import { useAcesso } from '@/hooks/use-acesso';
import { useResource } from '@/hooks/use-resource';
import { useSessao } from '@/hooks/use-sessao';
import {
  DESCRICAO_AUDIENCIA,
  INFO_FUNCIONALIDADES,
  ROTULO_AUDIENCIA,
  rotuloDoAlvo,
} from '@/lib/acesso';
import { podeAdministrar } from '@/lib/capabilities';
import { formatarDataHora, formatarRelativo, plural } from '@/lib/format';
import { cn } from '@/lib/utils';

/**
 * Página "Visibilidade" (/visibilidade): quem vê cada funcionalidade e cada
 * ramo. Gravação imediata e otimista (rollback + toast com a mensagem real da
 * API). Aba "Histórico" com quem mudou o quê. Exige `admin`.
 */

const TITULO = 'Visibilidade';
const DESCRICAO =
  'Decida quem enxerga cada funcionalidade e cada ramo. A mudança vale na hora. Quem opera o sistema (administradores, mantenedores e quem publica no painel) sempre enxerga tudo.';

type AbaId = 'configuracao' | 'historico';

const ABAS: Array<{ id: AbaId; rotulo: string; icone: typeof Eye }> = [
  { id: 'configuracao', rotulo: 'Configuração', icone: SlidersHorizontal },
  { id: 'historico', rotulo: 'Histórico', icone: History },
];

function nomeDoProduto(produto: string): string {
  return PRODUTOS[produto] ?? produto;
}

function textoDoErro(causa: unknown): string {
  return mensagemDoErro(causa) ?? (causa instanceof Error ? causa.message : String(causa));
}

const OPCOES = AUDIENCIAS.map((valor) => ({ valor, rotulo: ROTULO_AUDIENCIA[valor] }));

export function VisibilidadePage() {
  const { roles, rolesCarregando } = useSessao();
  const [aba, setAba] = React.useState<AbaId>('configuracao');
  // Muda a cada gravação com sucesso: o histórico se relê sozinho.
  const [versaoHistorico, setVersaoHistorico] = React.useState(0);
  const permitido = podeAdministrar(roles).permitido;

  if (rolesCarregando) return <TelaProtegidaCarregando titulo={TITULO} descricao={DESCRICAO} />;

  return (
    <PageBody>
      <PageHeader titulo={TITULO} descricao={DESCRICAO} />
      {!permitido ? (
        <EstadoVazio
          titulo="Sem acesso a Visibilidade"
          descricao='Esta tela exige a capability "admin". Peça a alguém com admin para conceder.'
        />
      ) : (
        <div className="flex flex-col gap-5">
          <div role="tablist" aria-label="Seções de Visibilidade" className="flex gap-1 border-b border-border">
            {ABAS.map((item) => {
              const Icone = item.icone;
              const ativa = aba === item.id;
              return (
                <button
                  key={item.id}
                  id={`vis-aba-${item.id}`}
                  type="button"
                  role="tab"
                  aria-selected={ativa}
                  aria-controls="vis-painel"
                  onClick={() => setAba(item.id)}
                  className={cn(
                    '-mb-px flex items-center gap-2 border-b-2 px-3.5 py-2.5 text-body transition-colors duration-fast ease-standard',
                    ativa ? 'border-primary font-bold text-primary' : 'border-transparent text-on-surface-muted hover:text-on-surface',
                  )}
                >
                  <Icone className="size-4 shrink-0" aria-hidden="true" />
                  {item.rotulo}
                </button>
              );
            })}
          </div>
          <div id="vis-painel" role="tabpanel" aria-labelledby={`vis-aba-${aba}`}>
            {aba === 'configuracao' ? (
              <Configuracao aoGravar={() => setVersaoHistorico((v) => v + 1)} />
            ) : (
              <Historico versao={versaoHistorico} />
            )}
          </div>
        </div>
      )}
    </PageBody>
  );
}

/* ------------------------------ Configuração ------------------------------ */

function Configuracao({ aoGravar }: { aoGravar: () => void }) {
  const { recarregar: recarregarAcesso } = useAcesso();
  const recurso = useResource<Visibilidade>((signal) => dataSource.listarVisibilidade(signal));
  const [gravando, setGravando] = React.useState<ReadonlySet<string>>(new Set());
  const [busca, setBusca] = React.useState('');

  // Sempre o estado mais novo, para dois cliques seguidos não desfazerem um ao outro no rollback.
  const ref = React.useRef(recurso);
  ref.current = recurso;

  function marcar(chave: string, ligado: boolean) {
    setGravando((atual) => {
      const proximo = new Set(atual);
      if (ligado) proximo.add(chave);
      else proximo.delete(chave);
      return proximo;
    });
  }

  async function alterarFuncionalidade(funcionalidade: Funcionalidade, audiencia: Audiencia) {
    const dados = ref.current.dados;
    const anterior = dados?.funcionalidades.find((item) => item.funcionalidade === funcionalidade);
    if (!dados || !anterior || anterior.audiencia === audiencia) return;
    const chave = `f:${funcionalidade}`;
    const aplicar = (parcial: Partial<(typeof dados.funcionalidades)[number]>) => {
      const atual = ref.current.dados;
      if (!atual) return;
      ref.current.definir({
        ...atual,
        funcionalidades: atual.funcionalidades.map((item) =>
          item.funcionalidade === funcionalidade ? { ...item, ...parcial } : item,
        ),
      });
    };
    marcar(chave, true);
    aplicar({ audiencia });
    try {
      aplicar(await dataSource.alterarVisibilidadeFuncionalidade(funcionalidade, audiencia));
      aoGravar();
      recarregarAcesso();
    } catch (causa) {
      aplicar({ audiencia: anterior.audiencia });
      toast.error(`Não foi possível alterar a visibilidade de ${rotulo(funcionalidade)}: ${textoDoErro(causa)}`);
    } finally {
      marcar(chave, false);
    }
  }

  async function alterarRamo(produto: string, ramo: string, audiencia: Audiencia) {
    const dados = ref.current.dados;
    const anterior = dados?.ramos.find((item) => item.produto === produto && item.ramo === ramo);
    if (!dados || !anterior || anterior.audiencia === audiencia) return;
    const chave = `r:${produto}:${ramo}`;
    const aplicar = (parcial: Partial<(typeof dados.ramos)[number]>) => {
      const atual = ref.current.dados;
      if (!atual) return;
      ref.current.definir({
        ...atual,
        ramos: atual.ramos.map((item) => (item.produto === produto && item.ramo === ramo ? { ...item, ...parcial } : item)),
      });
    };
    marcar(chave, true);
    aplicar({ audiencia });
    try {
      aplicar(await dataSource.alterarVisibilidadeRamo(produto, ramo, audiencia));
      aoGravar();
    } catch (causa) {
      aplicar({ audiencia: anterior.audiencia });
      toast.error(`Não foi possível alterar a visibilidade do ramo ${ramo}: ${textoDoErro(causa)}`);
    } finally {
      marcar(chave, false);
    }
  }

  const termo = busca.trim().toLowerCase();
  const ramos = (recurso.dados?.ramos ?? []).filter(
    (item) => !termo || item.ramo.toLowerCase().includes(termo) || nomeDoProduto(item.produto).toLowerCase().includes(termo),
  );

  if (recurso.estado === 'carregando') {
    return (
      <div className="flex flex-col gap-5" role="status" aria-label="Carregando a visibilidade">
        <Skeleton className="h-64 rounded-card" />
        <Skeleton className="h-64 rounded-card" />
      </div>
    );
  }
  if (recurso.estado === 'erro') {
    return (
      <Card>
        <EstadoErro erro={recurso.erro} aoTentarDeNovo={recurso.recarregar} />
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <Card as="section" aria-labelledby="vis-funcionalidades">
        <CardHeader className="flex-col items-start gap-1">
          <CardTitle id="vis-funcionalidades">Funcionalidades</CardTitle>
          <CardDescription>Quem enxerga cada área do Compilações na navegação.</CardDescription>
        </CardHeader>
        <CardContent className="gap-5">
          <ul aria-label="Significado de cada audiência" className="grid gap-2 sm:grid-cols-3">
            {AUDIENCIAS.map((audiencia) => (
              <li key={audiencia} className="rounded-input border border-border bg-bg-subtle px-3 py-2.5 text-cell">
                <span className="font-bold">{ROTULO_AUDIENCIA[audiencia]}</span>
                <span className="mt-0.5 block font-normal text-on-surface-muted">{DESCRICAO_AUDIENCIA[audiencia]}</span>
              </li>
            ))}
          </ul>
          <ul className="flex flex-col divide-y divide-border-subtle">
            {INFO_FUNCIONALIDADES.map((info) => {
              const linha = recurso.dados?.funcionalidades.find((item) => item.funcionalidade === info.id);
              if (!linha) return null;
              const Icone = info.icone;
              return (
                <li key={info.id} className="grid items-center gap-x-6 gap-y-2 py-3 first:pt-0 last:pb-0 md:grid-cols-[minmax(0,1fr)_auto]">
                  <div className="flex min-w-0 items-start gap-3">
                    <Icone className="mt-0.5 size-4 shrink-0 text-icon" aria-hidden="true" />
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-body font-bold">
                        {info.rotulo}
                        {linha.audiencia === 'oculto' ? (
                          <Badge tone="warning" title="Você enxerga porque opera o sistema (administradores, mantenedores e quem publica no painel).">
                            Oculto para os demais
                          </Badge>
                        ) : null}
                      </span>
                      <span className="text-cell text-on-surface-muted">{info.descricao}</span>
                      <span className="text-micro font-normal text-on-surface-muted" aria-live="polite">
                        {DESCRICAO_AUDIENCIA[linha.audiencia]}
                        {linha.atualizado_em ? (
                          <> Alterado por {linha.atualizado_por ?? 'alguém'} {formatarRelativo(linha.atualizado_em)}.</>
                        ) : null}
                      </span>
                    </div>
                  </div>
                  <Segmentado
                    rotulo={`Audiência de ${info.rotulo}`}
                    valor={linha.audiencia}
                    opcoes={OPCOES}
                    desabilitado={gravando.has(`f:${info.id}`)}
                    aoMudar={(valor) => void alterarFuncionalidade(info.id, valor)}
                    className="w-full md:w-auto"
                  />
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>

      <Card as="section" aria-labelledby="vis-ramos">
        <CardHeader className="flex-col items-start gap-1">
          <CardTitle id="vis-ramos">Ramos</CardTitle>
          <CardDescription>
            Esconda um ramo específico (por exemplo, uma sprint em andamento). “Público” é o padrão: sem restrição.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Field id="vis-busca-ramo" label="Buscar ramo" className="max-w-sm">
            {(props) => (
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-icon" aria-hidden="true" />
                <Input
                  {...props}
                  type="search"
                  value={busca}
                  placeholder="Ex.: 2602"
                  onChange={(evento) => setBusca(evento.target.value)}
                  className="pl-9"
                />
              </div>
            )}
          </Field>

          {(recurso.dados?.ramos.length ?? 0) === 0 ? (
            <EstadoVazio titulo="Nenhum ramo ainda" descricao="Os ramos aparecem assim que o Jenkins publica a primeira versão." className="py-8" />
          ) : ramos.length === 0 ? (
            <EstadoVazio titulo="Nenhum ramo encontrado" descricao={`Nada corresponde a "${busca.trim()}".`} className="py-8" />
          ) : (
            <ul aria-label="Ramos" className="flex flex-col divide-y divide-border-subtle rounded-input border border-border">
              {ramos.map((item) => (
                <li
                  key={`${item.produto}/${item.ramo}`}
                  className="grid items-center gap-x-6 gap-y-2 px-3 py-3 md:grid-cols-[minmax(0,1fr)_auto]"
                >
                  <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-0.5">
                    <span className="font-mono text-body font-bold tabular-nums">{item.ramo}</span>
                    <span className="text-cell text-on-surface-muted">{nomeDoProduto(item.produto)}</span>
                    <span className="cmp-eyebrow">{plural(item.total_builds, 'versão', 'versões')}</span>
                    {item.audiencia !== 'publico' ? (
                      <Badge tone={item.audiencia === 'oculto' ? 'danger' : 'warning'}>{ROTULO_AUDIENCIA[item.audiencia]}</Badge>
                    ) : null}
                  </div>
                  <Segmentado
                    rotulo={`Audiência do ramo ${item.ramo}`}
                    compacto
                    valor={item.audiencia}
                    opcoes={OPCOES}
                    desabilitado={gravando.has(`r:${item.produto}:${item.ramo}`)}
                    aoMudar={(valor) => void alterarRamo(item.produto, item.ramo, valor)}
                    className="w-full md:w-auto"
                  />
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function rotulo(funcionalidade: Funcionalidade): string {
  return INFO_FUNCIONALIDADES.find((item) => item.id === funcionalidade)?.rotulo ?? funcionalidade;
}

/* -------------------------------- Histórico -------------------------------- */

function Historico({ versao }: { versao: number }) {
  const eventos = useResource<EventoVisibilidade[]>((signal) => dataSource.listarEventosVisibilidade(50, signal), [versao]);

  return (
    <Card>
      {eventos.estado === 'carregando' ? (
        <ul role="status" aria-label="Carregando histórico" className="flex flex-col gap-4 p-4">
          {Array.from({ length: 4 }).map((_, indice) => (
            <li key={indice} className="flex flex-col gap-1.5">
              <Skeleton className="h-3.5 w-3/4 max-w-md" />
              <Skeleton className="h-2.5 w-24" />
            </li>
          ))}
        </ul>
      ) : eventos.estado === 'erro' ? (
        <EstadoErro erro={eventos.erro} aoTentarDeNovo={eventos.recarregar} />
      ) : eventos.dados && eventos.dados.length > 0 ? (
        <ol aria-label="Mudanças de visibilidade" className="divide-y divide-border-subtle">
          {eventos.dados.map((evento) => (
            <li key={evento.id} className="flex flex-col gap-1 px-4 py-3">
              <p className="flex flex-wrap items-center gap-x-2 gap-y-1 break-words text-body">
                <strong>{evento.usuario}</strong>
                <span>alterou {rotuloDoAlvo(evento.alvo, nomeDoProduto)}:</span>
                {evento.audiencia_anterior ? (
                  <Badge tone="neutral">{ROTULO_AUDIENCIA[evento.audiencia_anterior]}</Badge>
                ) : null}
                <span aria-label="para">→</span>
                <Badge tone="primary">{ROTULO_AUDIENCIA[evento.audiencia_nova]}</Badge>
              </p>
              <time
                dateTime={evento.criado_em}
                title={formatarDataHora(evento.criado_em)}
                className="text-micro font-normal text-on-surface-muted"
              >
                {formatarRelativo(evento.criado_em)}
              </time>
            </li>
          ))}
        </ol>
      ) : (
        <EstadoVazio
          titulo="Nenhuma mudança registrada"
          descricao="Quando alguém alterar a visibilidade de uma funcionalidade ou de um ramo, aparece aqui, com quem fez e quando."
        />
      )}
    </Card>
  );
}
